import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { auth, db, functions } from './firebase';
import { doc, getDoc, updateDoc, setDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { signOut } from "firebase/auth";
import './SettingsPage.css';
import { usePostHog } from 'posthog-js/react';
import DiscordButton from './components/DiscordButton';
import RedditButton from './components/RedditButton';
import AutoUploadSettings from './components/AutoUploadSettings';

import stravaConnectButton from './assets/strava/btn_strava_connect_with_orange.svg';
import { useAdminView } from './contexts/AdminViewContext'; // Use admin view context

// Strava OAuth Configuration
const STRAVA_CLIENT_ID = "159733";
const STRAVA_SIGNIN_REDIRECT_URI = window.location.origin + "/strava-auth-callback";
const STRAVA_SCOPE = "activity:read,activity:read_all,activity:write";

function SettingsPage({ user }) {
    const { t } = useTranslation();
    const { isAdmin } = useAdminView(); // Use context for admin status
    const [stravaConnected, setStravaConnected] = useState(false);
    const [isCheckingStrava, setIsCheckingStrava] = useState(true);
    const [autoAppendLink, setAutoAppendLink] = useState(false);
    const [isLoadingSettings, setIsLoadingSettings] = useState(true);
    const [isSavingSettings, setIsSavingSettings] = useState(false);
    const [routeLineWidth, setRouteLineWidth] = useState(5);
    const [routeLineColor, setRouteLineColor] = useState("#FC5200");
    const [routeLineOpacity, setRouteLineOpacity] = useState(0.75);
    const [monthlyLimit, setMonthlyLimit] = useState(10);
    const [isLoadingAdminSettings, setIsLoadingAdminSettings] = useState(false);
    const [isSavingAdminSettings, setIsSavingAdminSettings] = useState(false);
    const [waterThreshold, setWaterThreshold] = useState(5.0);
    const [monthlyResetEnabled, setMonthlyResetEnabled] = useState(false);
    const [emailRegistrationCredits, setEmailRegistrationCredits] = useState(5);
    const [badgesVisibleToAll, setBadgesVisibleToAll] = useState(false);

    // Account deletion state
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [isDeletingAccount, setIsDeletingAccount] = useState(false);

    // Watermark detection settings
    const [watermarkSettings, setWatermarkSettings] = useState({
        confidenceThreshold: 0.6,
        uniformityRatioThreshold: 0.75,
        colorTolerance: 20,
        resolutionMatchWeight: 0.5,
        watermarkConfidenceWeight: 0.5
    });
    const [isLoadingWatermarkSettings, setIsLoadingWatermarkSettings] = useState(false);
    const [isSavingWatermarkSettings, setIsSavingWatermarkSettings] = useState(false);

    const posthog = usePostHog();
    const navigate = useNavigate();

    // Predefined color options
    const colorOptions = [
        { name: "Strava Orange", value: "#FC5200" },
        { name: "Electric Blue", value: "#007BFF" },
        { name: "Forest Green", value: "#28A745" },
        { name: "Crimson Red", value: "#DC3545" },
        { name: "Purple", value: "#6F42C1" },
        { name: "Teal", value: "#20C997" },
        { name: "Gold", value: "#FFC107" },
        { name: "Dark Gray", value: "#495057" },
    ];

    useEffect(() => {
        if (user) {
            checkStravaConnection(user.uid);
            loadUserSettings(user.uid);
            if (isAdmin) {
                loadAdminSettings();
                loadWatermarkSettings();
            }
        } else {
            setStravaConnected(false);
            setIsCheckingStrava(false);
            setIsLoadingSettings(false);
        }
    }, [user, isAdmin]);

    const checkStravaConnection = async (userId) => {
        setIsCheckingStrava(true);
        try {
            const tokenDocRef = doc(db, 'users', userId, 'stravaTokens', 'tokens');
            const docSnap = await getDoc(tokenDocRef);
            setStravaConnected(docSnap.exists());
        } catch (error) {
            console.error("Error checking Strava connection:", error);
            setStravaConnected(false);
        } finally {
            setIsCheckingStrava(false);
        }
    };

    const loadUserSettings = async (userId) => {
        setIsLoadingSettings(true);
        try {
            const userDocRef = doc(db, 'users', userId);
            const docSnap = await getDoc(userDocRef);
            if (docSnap.exists()) {
                const userData = docSnap.data();
                setAutoAppendLink(userData?.settings?.autoAppendMythicLink === true);
            }
        } catch (error) {
            console.error("Error loading user settings:", error);
        } finally {
            setIsLoadingSettings(false);
        }
    };


    const updateAutoAppendSetting = async (newValue) => {
        posthog?.capture('setting_changed', {
            setting_name: 'auto_append_to_strava',
            new_value: newValue,
            old_value: autoAppendLink
        });

        if (!user) return;

        setIsSavingSettings(true);
        try {
            const userDocRef = doc(db, 'users', user.uid);
            await updateDoc(userDocRef, {
                'settings.autoAppendMythicLink': newValue
            });
            setAutoAppendLink(newValue);
        } catch (error) {
            console.error("Error updating auto-append setting:", error);
            alert("Failed to update setting. Please try again.");
        } finally {
            setIsSavingSettings(false);
        }
    };

    const loadAdminSettings = async () => {
        setIsLoadingAdminSettings(true);
        try {
            const settingsDocRef = doc(db, 'settings', 'mapGeneration');
            const docSnap = await getDoc(settingsDocRef);
            if (docSnap.exists()) {
                const settingsData = docSnap.data();
                setRouteLineWidth(settingsData?.routeLineWidth || 5);
                setRouteLineColor(settingsData?.routeLineColor || "#FC5200");
                setRouteLineOpacity(settingsData?.routeLineOpacity || 0.75);
                setMonthlyLimit(settingsData?.monthlyLimit || 10);
                setWaterThreshold(settingsData?.waterThreshold || 5.0);
                setMonthlyResetEnabled(settingsData?.monthlyResetEnabled || false);
                setEmailRegistrationCredits(settingsData?.emailRegistrationCredits || 5);
                setBadgesVisibleToAll(settingsData?.badgesVisibleToAll || false);
            } else {
                setRouteLineWidth(5); // Default value
                setRouteLineColor("#FC5200");
                setRouteLineOpacity(0.75);
                setMonthlyLimit(10);
                setWaterThreshold(5.0);
                setMonthlyResetEnabled(false);
                setEmailRegistrationCredits(5);
                setBadgesVisibleToAll(false);
            }
        } catch (error) {
            console.error("Error loading admin settings:", error);
            setRouteLineWidth(5); // Default on error
            setRouteLineColor("#FC5200");
            setRouteLineOpacity(0.75);
            setMonthlyLimit(10);
            setWaterThreshold(5.0);
            setMonthlyResetEnabled(false);
            setEmailRegistrationCredits(5);
            setBadgesVisibleToAll(false);
        } finally {
            setIsLoadingAdminSettings(false);
        }
    };

    const loadWatermarkSettings = async () => {
        setIsLoadingWatermarkSettings(true);
        try {
            const { httpsCallable } = await import("firebase/functions");
            const { functions } = await import("./firebase");
            const getWatermarkSettingsFunc = httpsCallable(functions, 'getWatermarkSettings');
            const result = await getWatermarkSettingsFunc();

            if (result.data?.settings) {
                setWatermarkSettings(result.data.settings);
            }
        } catch (error) {
            console.error("Error loading watermark settings:", error);
        } finally {
            setIsLoadingWatermarkSettings(false);
        }
    };

    const updateWatermarkSettings = async (settings) => {
        posthog?.capture('admin_watermark_settings_changed', {
            settings: settings
        });

        if (!user || !isAdmin) return;

        setIsSavingWatermarkSettings(true);
        try {
            const { httpsCallable } = await import("firebase/functions");
            const { functions } = await import("./firebase");
            const updateWatermarkSettingsFunc = httpsCallable(functions, 'updateWatermarkSettings');
            await updateWatermarkSettingsFunc({ settings });

            setWatermarkSettings(settings);
            alert("Watermark detection settings updated successfully!");
        } catch (error) {
            console.error("Error updating watermark settings:", error);
            alert("Failed to update watermark settings. Please try again.");
        } finally {
            setIsSavingWatermarkSettings(false);
        }
    };

    const updateRouteLineWidth = async (newWidth) => {
        posthog?.capture('admin_setting_changed', {
            setting_name: 'route_line_width',
            new_value: newWidth,
            old_value: routeLineWidth
        });

        if (!user || !isAdmin) return;

        setIsSavingAdminSettings(true);
        try {
            const settingsDocRef = doc(db, 'settings', 'mapGeneration');
            await setDoc(settingsDocRef, {
                routeLineWidth: newWidth
            }, { merge: true });
            setRouteLineWidth(newWidth);
        } catch (error) {
            console.error("Error updating route line width:", error);
            alert("Failed to update route line width setting. Please try again.");
        } finally {
            setIsSavingAdminSettings(false);
        }
    };

    const updateRouteLineColor = async (newColor) => {
        posthog?.capture('admin_setting_changed', {
            setting_name: 'route_line_color',
            new_value: newColor,
            old_value: routeLineColor
        });

        if (!user || !isAdmin) return;

        setIsSavingAdminSettings(true);
        try {
            const settingsDocRef = doc(db, 'settings', 'mapGeneration');
            await setDoc(settingsDocRef, {
                routeLineColor: newColor
            }, { merge: true });
            setRouteLineColor(newColor);
        } catch (error) {
            console.error("Error updating route line color:", error);
            alert("Failed to update route line color setting. Please try again.");
        } finally {
            setIsSavingAdminSettings(false);
        }
    };

    const updateRouteLineOpacity = async (newOpacity) => {
        posthog?.capture('admin_setting_changed', {
            setting_name: 'route_line_opacity',
            new_value: newOpacity,
            old_value: routeLineOpacity
        });

        if (!user || !isAdmin) return;

        setIsSavingAdminSettings(true);
        try {
            const settingsDocRef = doc(db, 'settings', 'mapGeneration');
            await setDoc(settingsDocRef, {
                routeLineOpacity: newOpacity
            }, { merge: true });
            setRouteLineOpacity(newOpacity);
        } catch (error) {
            console.error("Error updating route line opacity:", error);
            alert("Failed to update route line opacity setting. Please try again.");
        } finally {
            setIsSavingAdminSettings(false);
        }
    };

    const updateMonthlyLimit = async (newLimit) => {
        posthog?.capture('admin_setting_changed', {
            setting_name: 'monthly_limit',
            new_value: newLimit,
            old_value: monthlyLimit
        });

        if (!user || !isAdmin) return;

        setIsSavingAdminSettings(true);
        try {
            const settingsDocRef = doc(db, 'settings', 'mapGeneration');
            await setDoc(settingsDocRef, {
                monthlyLimit: newLimit
            }, { merge: true });
            setMonthlyLimit(newLimit);
        } catch (error) {
            console.error("Error updating monthly limit:", error);
            alert("Failed to update monthly limit setting. Please try again.");
        } finally {
            setIsSavingAdminSettings(false);
        }
    };

    const updateWaterThreshold = async (newThreshold) => {
        posthog?.capture('admin_setting_changed', {
            setting_name: 'water_threshold',
            new_value: newThreshold,
            old_value: waterThreshold
        });

        if (!user || !isAdmin) return;

        setIsSavingAdminSettings(true);
        try {
            const settingsDocRef = doc(db, 'settings', 'mapGeneration');
            await setDoc(settingsDocRef, {
                waterThreshold: newThreshold
            }, { merge: true });
            setWaterThreshold(newThreshold);
        } catch (error) {
            console.error("Error updating water threshold:", error);
            alert("Failed to update water threshold setting. Please try again.");
        } finally {
            setIsSavingAdminSettings(false);
        }
    };

    const updateMonthlyResetEnabled = async (enabled) => {
        posthog?.capture('admin_setting_changed', {
            setting_name: 'monthly_reset_enabled',
            new_value: enabled,
            old_value: monthlyResetEnabled
        });

        if (!user || !isAdmin) return;

        setIsSavingAdminSettings(true);
        try {
            const { httpsCallable } = await import("firebase/functions");
            const { functions } = await import("./firebase");
            const updateMonthlyResetEnabledFunc = httpsCallable(functions, 'updateMonthlyResetEnabled');

            await updateMonthlyResetEnabledFunc({ enabled });
            setMonthlyResetEnabled(enabled);
        } catch (error) {
            console.error("Error updating monthly reset enabled:", error);
            alert("Failed to update monthly reset setting. Please try again.");
        } finally {
            setIsSavingAdminSettings(false);
        }
    };

    const updateEmailRegistrationCredits = async (newCredits) => {
        posthog?.capture('admin_setting_changed', {
            setting_name: 'email_registration_credits',
            new_value: newCredits,
            old_value: emailRegistrationCredits
        });

        if (!user || !isAdmin) return;

        setIsSavingAdminSettings(true);
        try {
            const settingsDocRef = doc(db, 'settings', 'mapGeneration');
            await setDoc(settingsDocRef, {
                emailRegistrationCredits: newCredits
            }, { merge: true });
            setEmailRegistrationCredits(newCredits);
            alert("Email registration credits updated successfully!");
        } catch (error) {
            console.error("Error updating email registration credits:", error);
            alert("Failed to update email registration credits. Please try again.");
        } finally {
            setIsSavingAdminSettings(false);
        }
    };

    const updateBadgesVisibleToAll = async (visible) => {
        posthog?.capture('admin_setting_changed', {
            setting_name: 'badges_visible_to_all',
            new_value: visible,
            old_value: badgesVisibleToAll
        });

        if (!user || !isAdmin) return;

        setIsSavingAdminSettings(true);
        try {
            const settingsDocRef = doc(db, 'settings', 'mapGeneration');
            await setDoc(settingsDocRef, {
                badgesVisibleToAll: visible
            }, { merge: true });
            setBadgesVisibleToAll(visible);
            alert("Badge visibility setting updated successfully!");
        } catch (error) {
            console.error("Error updating badge visibility:", error);
            alert("Failed to update badge visibility setting. Please try again.");
        } finally {
            setIsSavingAdminSettings(false);
        }
    };

    const handleStravaConnect = () => {
        posthog?.capture('strava_connect_clicked', {
            page: 'settings',
            user_authenticated: !!user
        });
        if (!STRAVA_CLIENT_ID || STRAVA_CLIENT_ID === "YOUR_STRAVA_CLIENT_ID") {
            alert("Strava Client ID placeholder is still in use. Please update it.");
            return;
        }

        const state = Math.random().toString(36).substring(2);
        localStorage.setItem('stravaOAuthState', state);

        const consentUrl = `https://www.strava.com/oauth/authorize?client_id=${STRAVA_CLIENT_ID}&redirect_uri=${encodeURIComponent(STRAVA_SIGNIN_REDIRECT_URI)}&response_type=code&scope=${encodeURIComponent(STRAVA_SCOPE)}&approval_prompt=auto&state=${state}`;
        window.location.href = consentUrl;
    };

    const handleStravaDisconnect = async () => {
        posthog?.capture('strava_disconnect_clicked', {
            page: 'settings'
        });
        if (!user) {
            alert("You must be signed in to disconnect Strava.");
            return;
        }
        if (window.confirm("Are you sure you want to disconnect your Strava account from Mythic Maps?")) {
            try {
                const { httpsCallable } = await import("firebase/functions");
                const { functions } = await import("./firebase");
                const disconnectStravaFunc = httpsCallable(functions, 'disconnectStrava');
                await disconnectStravaFunc();
                alert("Strava account disconnected successfully.");
                setStravaConnected(false);
                posthog?.capture('strava_disconnected_success');
            } catch (error) {
                console.error("Error disconnecting Strava:", error);
                alert(`Failed to disconnect Strava: ${error.message}`);
                posthog?.capture('strava_disconnect_failed', {
                    error_message: error.message
                });
            }
        }
    };

    const handleDeleteAccount = async () => {
        if (deleteConfirmText !== 'DELETE') return;

        setIsDeletingAccount(true);

        try {
            const deleteUserAccountFunc = httpsCallable(functions, 'deleteUserAccount');
            const result = await deleteUserAccountFunc();

            if (result.data.success) {
                posthog?.capture('account_deleted', {
                    generations_deleted: result.data.summary.generationsDeleted,
                    files_deleted: result.data.summary.storageFilesDeleted
                });

                // Sign out and redirect to home
                await signOut(auth);
                navigate('/');
            }
        } catch (error) {
            console.error("Error deleting account:", error);
            alert("Failed to delete account. Please try again or contact support.");
        } finally {
            setIsDeletingAccount(false);
            setShowDeleteModal(false);
            setDeleteConfirmText('');
        }
    };

    // Listen for Strava auth success messages
    useEffect(() => {
        const handleStravaSuccess = (event) => {
            if (event.origin !== window.location.origin || event.data !== "stravaAuthSuccess") return;
            if (user) checkStravaConnection(user.uid);
        };
        window.addEventListener("message", handleStravaSuccess);
        return () => window.removeEventListener("message", handleStravaSuccess);
    }, [user]);


    if (!user) {
        return (
            <div className="container settings-page">
                <p style={{ textAlign: 'center' }}>{t('common.signInToAccess', { page: t('settings.title', 'Settings') })}</p>
            </div>
        );
    }

    return (
        <div className="container settings-page">
            <div className="settings-header">
                <h1>{t('settings.title')}</h1>
                <p className="settings-subtitle">{t('settings.subtitle')}</p>
            </div>
            <div className="settings-divider"></div>

            {/* Section 1: Manage Strava Connection */}
            <div className="settings-section">
                <h2>
                    <i className="ti ti-brand-strava" style={{ fontSize: '24px', marginRight: '8px', color: 'var(--Brand-Strava-Orange)' }}></i>
                    {t('settings.manageStravaConnection')}
                </h2>
                <p className="section-description">{t('settings.stravaDesc')}</p>

                <div className="strava-connection-card">
                    <div className="connection-status">
                        <div className="status-indicator">
                            {isCheckingStrava ? (
                                <div className="status-loading">
                                    <div className="loading-spinner-small"></div>
                                    <span>{t('settings.checkingConnection')}</span>
                                </div>
                            ) : (
                                <>
                                    <div className={`status-dot ${stravaConnected ? 'connected' : 'disconnected'}`}></div>
                                    <span className="status-text">
                                        {stravaConnected ? t('settings.connectedToStrava') : t('settings.notConnectedToStrava')}
                                    </span>
                                </>
                            )}
                        </div>

                        <div className="connection-actions">
                            {!stravaConnected ? (
                                <button onClick={handleStravaConnect} className="strava-connect-button-wrapper">
                                    <img src={stravaConnectButton} alt="Connect with Strava" className="strava-connect-btn-img" />
                                </button>
                            ) : (
                                <button onClick={handleStravaDisconnect} className="strava-disconnect-button">
                                    <i className="ti ti-unlink" style={{ fontSize: '16px', marginRight: '8px' }}></i>
                                    {t('settings.disconnectStrava')}
                                </button>
                            )}
                        </div>
                    </div>

                    {stravaConnected && (
                        <>
                            <div className="connection-info">
                                <p>
                                    <i className="ti ti-info-circle" style={{ fontSize: '16px', marginRight: '8px', color: 'var(--Primary-600)' }}></i>
                                    {t('settings.stravaConnectedInfo')}
                                </p>
                            </div>

                        </>
                    )}
                </div>
            </div>

            {/* Section 2: Strava Integration Preferences */}
            {stravaConnected && (
                <div className="settings-section">
                    <h2>
                        <i className="ti ti-settings" style={{ fontSize: '24px', marginRight: '8px', color: 'var(--Primary-600)' }}></i>
                        {t('settings.integrationPrefs')}
                    </h2>
                    <p className="section-description">{t('settings.integrationPrefsDesc')}</p>

                    <div className="preference-card">
                        <div className="preference-item">
                            <div className="preference-info">
                                <h4>{t('settings.autoAppendTitle')}</h4>
                                <p>{t('settings.autoAppendDesc')}</p>
                            </div>
                            <div className="preference-control">
                                {isLoadingSettings ? (
                                    <div className="loading-spinner-small"></div>
                                ) : (
                                    <label className="toggle-switch">
                                        <input
                                            type="checkbox"
                                            checked={autoAppendLink}
                                            onChange={(e) => updateAutoAppendSetting(e.target.checked)}
                                            disabled={isSavingSettings}
                                        />
                                        <span className="toggle-slider"></span>
                                    </label>
                                )}
                            </div>
                        </div>

                        {/* {autoAppendLink && (
                            <div className="preference-note">
                                <i className="ti ti-info-circle" style={{ fontSize: '16px', marginRight: '8px', color: 'var(--Primary-600)' }}></i>
                                Links will be added to new activities with the format: https://mythicmaps.ai/generate/activity/[activity-id]
                            </div>
                        )} */}
                    </div>

                    <div className="preference-card">
                        <AutoUploadSettings user={user} />
                    </div>
                </div>
            )}

            {/* Section 3: Credits Management */}
            <div className="settings-section">
                <h2>
                    <i className="ti ti-coins" style={{ fontSize: '24px', marginRight: '8px', color: 'var(--Primary-600)' }}></i>
                    {t('settings.mapGenerationCredits')}
                </h2>
                <p className="section-description">{t('settings.mapGenerationCreditsDesc')}</p>

                <div className="preference-card">
                    <div className="preference-item">
                        <div className="preference-info">
                                <h4>{t('settings.viewManageCreditsTitle')}</h4>
                                <p>{t('settings.viewManageCreditsDesc')}</p>
                        </div>
                        <div className="preference-control">
                            <Link
                                to="/credits"
                                className="settings-link-button"
                                style={{
                                    padding: '10px 20px',
                                    background: 'var(--Primary-600, #57534E)',
                                    color: 'var(--Base-White, #FFF)',
                                    borderRadius: 'var(--border-radius-md, 8px)',
                                    textDecoration: 'none',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    transition: 'background 0.2s'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = 'var(--Primary-700, #3E3A37)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'var(--Primary-600, #57534E)';
                                }}
                            >
                                <i className="ti ti-arrow-right" style={{ fontSize: '16px' }}></i>
                                {t('settings.goToCredits')}
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

            {/* Section 5: Admin Settings (Only for Admin Users) */}
            {isAdmin && (
                <div className="settings-section">
                    <h2>
                        <i className="ti ti-shield-check" style={{ fontSize: '24px', marginRight: '8px', color: 'var(--Primary-600)' }}></i>
                        {t('settings.adminSettings')}
                    </h2>
                    <p className="section-description">{t('settings.adminSettingsDesc')}</p>

                    <div className="preference-card">
                        <div className="preference-item">
                            <div className="preference-info">
                                <h4>{t('settings.routeLineWidth')}</h4>
                                <p>{t('settings.routeLineWidthDesc')}</p>
                            </div>
                            <div className="preference-control">
                                {isLoadingAdminSettings ? (
                                    <div className="loading-spinner-small"></div>
                                ) : (
                                    <div className="numeric-input-group">
                                        <input
                                            type="number"
                                            min="1"
                                            max="20"
                                            value={routeLineWidth}
                                            onChange={(e) => setRouteLineWidth(parseInt(e.target.value) || 5)}
                                            disabled={isSavingAdminSettings}
                                            className="numeric-input"
                                        />
                                        <span className="input-unit">px</span>
                                        <button
                                            onClick={() => updateRouteLineWidth(routeLineWidth)}
                                            disabled={isSavingAdminSettings}
                                            className="save-button"
                                        >
                                            {isSavingAdminSettings ? t('common.saving') : t('common.save')}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="preference-note">
                            <i className="ti ti-info-circle" style={{ fontSize: '16px', marginRight: '8px', color: 'var(--Primary-600)' }}></i>
                            {t('settings.currentValuePx', { value: routeLineWidth })}
                        </div>
                    </div>

                    <div className="preference-card">
                        <div className="preference-item">
                            <div className="preference-info">
                                <h4>{t('settings.routeLineColor')}</h4>
                                <p>{t('settings.routeLineColorDesc')}</p>
                            </div>
                            <div className="preference-control">
                                {isLoadingAdminSettings ? (
                                    <div className="loading-spinner-small"></div>
                                ) : (
                                    <div className="color-input-group">
                                        <select
                                            value={routeLineColor}
                                            onChange={(e) => setRouteLineColor(e.target.value)}
                                            disabled={isSavingAdminSettings}
                                            className="color-select"
                                        >
                                            {colorOptions.map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.name}
                                                </option>
                                            ))}
                                        </select>
                                        <div
                                            className="color-preview"
                                            style={{ backgroundColor: routeLineColor }}
                                            title={`Preview: ${routeLineColor}`}
                                        ></div>
                                        <button
                                            onClick={() => updateRouteLineColor(routeLineColor)}
                                            disabled={isSavingAdminSettings}
                                            className="save-button"
                                        >
                                            {isSavingAdminSettings ? t('common.saving') : t('common.save')}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="preference-note">
                            <i className="ti ti-info-circle" style={{ fontSize: '16px', marginRight: '8px', color: 'var(--Primary-600)' }}></i>
                            {t('settings.currentValue', { value: routeLineColor })}
                        </div>
                    </div>

                    <div className="preference-card">
                        <div className="preference-item">
                            <div className="preference-info">
                                <h4>{t('settings.routeLineOpacity')}</h4>
                                <p>{t('settings.routeLineOpacityDesc')}</p>
                            </div>
                            <div className="preference-control">
                                {isLoadingAdminSettings ? (
                                    <div className="loading-spinner-small"></div>
                                ) : (
                                    <div className="numeric-input-group">
                                        <input
                                            type="number"
                                            min="0"
                                            max="1"
                                            step="0.01"
                                            value={routeLineOpacity}
                                            onChange={(e) => setRouteLineOpacity(parseFloat(e.target.value) || 0.75)}
                                            disabled={isSavingAdminSettings}
                                            className="numeric-input"
                                        />
                                        <span className="input-unit">opacity</span>
                                        <button
                                            onClick={() => updateRouteLineOpacity(routeLineOpacity)}
                                            disabled={isSavingAdminSettings}
                                            className="save-button"
                                        >
                                            {isSavingAdminSettings ? t('common.saving') : t('common.save')}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="preference-note">
                            <i className="ti ti-info-circle" style={{ fontSize: '16px', marginRight: '8px', color: 'var(--Primary-600)' }}></i>
                            {t('settings.currentValue', { value: routeLineOpacity.toFixed(2) })}
                        </div>
                    </div>

                    <div className="preference-card">
                        <div className="preference-item">
                            <div className="preference-info">
                                <h4>{t('settings.monthlyLimit')}</h4>
                                <p>{t('settings.monthlyLimitDesc')}</p>
                            </div>
                            <div className="preference-control">
                                {isLoadingAdminSettings ? (
                                    <div className="loading-spinner-small"></div>
                                ) : (
                                    <div className="numeric-input-group">
                                        <input
                                            type="number"
                                            min="1"
                                            max="50"
                                            value={monthlyLimit}
                                            onChange={(e) => setMonthlyLimit(parseInt(e.target.value) || 10)}
                                            disabled={isSavingAdminSettings}
                                            className="numeric-input"
                                        />
                                        <span className="input-unit">{t('settings.mapsUnit')}</span>
                                        <button
                                            onClick={() => updateMonthlyLimit(monthlyLimit)}
                                            disabled={isSavingAdminSettings}
                                            className="save-button"
                                        >
                                            {isSavingAdminSettings ? t('common.saving') : t('common.save')}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="preference-note">
                            <i className="ti ti-info-circle" style={{ fontSize: '16px', marginRight: '8px', color: 'var(--Primary-600)' }}></i>
                            {t('settings.currentValueWithUnit', { value: monthlyLimit, unit: t('settings.mapsUnit') })}
                        </div>
                    </div>

                    <div className="preference-card">
                        <div className="preference-item">
                            <div className="preference-info">
                                <h4>{t('settings.waterThreshold')}</h4>
                                <p>{t('settings.waterThresholdDesc')}</p>
                            </div>
                            <div className="preference-control">
                                {isLoadingAdminSettings ? (
                                    <div className="loading-spinner-small"></div>
                                ) : (
                                    <div className="numeric-input-group">
                                        <input
                                            type="number"
                                            min="0.1"
                                            max="50"
                                            step="0.1"
                                            value={waterThreshold}
                                            onChange={(e) => setWaterThreshold(parseFloat(e.target.value) || 5.0)}
                                            disabled={isSavingAdminSettings}
                                            className="numeric-input"
                                        />
                                        <span className="input-unit">%</span>
                                        <button
                                            onClick={() => updateWaterThreshold(waterThreshold)}
                                            disabled={isSavingAdminSettings}
                                            className="save-button"
                                        >
                                            {isSavingAdminSettings ? t('common.saving') : t('common.save')}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="preference-note">
                            <i className="ti ti-info-circle" style={{ fontSize: '16px', marginRight: '8px', color: 'var(--Primary-600)' }}></i>
                            {t('settings.currentValuePercent', { value: waterThreshold })}
                        </div>
                    </div>

                    <div className="preference-card">
                        <div className="preference-item">
                            <div className="preference-info">
                                <h4>{t('settings.monthlyReset')}</h4>
                                <p>{t('settings.monthlyResetDesc')}</p>
                            </div>
                            <div className="preference-control">
                                {isLoadingAdminSettings ? (
                                    <div className="loading-spinner-small"></div>
                                ) : (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <label className="toggle-switch">
                                            <input
                                                type="checkbox"
                                                checked={monthlyResetEnabled}
                                                onChange={(e) => updateMonthlyResetEnabled(e.target.checked)}
                                                disabled={isSavingAdminSettings}
                                            />
                                            <span className="toggle-slider"></span>
                                        </label>
                                        <span style={{
                                            color: monthlyResetEnabled ? 'var(--Green-600, #16A34A)' : 'var(--Gray-500, #667085)',
                                            fontWeight: '500',
                                            fontSize: '14px'
                                        }}>
                                            {monthlyResetEnabled ? t('common.enabled') : t('common.disabled')}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="preference-note">
                            <i className="ti ti-info-circle" style={{ fontSize: '16px', marginRight: '8px', color: 'var(--Primary-600)' }}></i>
                            {monthlyResetEnabled ? t('settings.resetMonthly') : t('settings.accumulateIndefinitely')}
                        </div>
                    </div>

                    <div className="preference-card">
                        <div className="preference-item">
                            <div className="preference-info">
                                <h4>{t('settings.emailRegistrationCredits')}</h4>
                                <p>{t('settings.emailRegistrationCreditsDesc')}</p>
                            </div>
                            <div className="preference-control">
                                {isLoadingAdminSettings ? (
                                    <div className="loading-spinner-small"></div>
                                ) : (
                                    <div className="numeric-input-group">
                                        <input
                                            type="number"
                                            min="1"
                                            max="20"
                                            value={emailRegistrationCredits}
                                            onChange={(e) => setEmailRegistrationCredits(parseInt(e.target.value) || 5)}
                                            disabled={isSavingAdminSettings}
                                            className="numeric-input"
                                        />
                                        <span className="input-unit">{t('settings.creditsUnit')}</span>
                                        <button
                                            onClick={() => updateEmailRegistrationCredits(emailRegistrationCredits)}
                                            disabled={isSavingAdminSettings}
                                            className="save-button"
                                        >
                                            {isSavingAdminSettings ? t('common.saving') : t('common.save')}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="preference-note">
                            <i className="ti ti-info-circle" style={{ fontSize: '16px', marginRight: '8px', color: 'var(--Primary-600)' }}></i>
                            {t('settings.currentValueWithUnit', { value: emailRegistrationCredits, unit: t('settings.creditsUnit') })}
                        </div>
                    </div>

                    <div className="preference-card">
                        <div className="preference-item">
                            <div className="preference-info">
                                <h4>{t('settings.badgesVisibility')}</h4>
                                <p>{t('settings.badgesVisibilityDesc')}</p>
                            </div>
                            <div className="preference-control">
                                {isLoadingAdminSettings ? (
                                    <div className="loading-spinner-small"></div>
                                ) : (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <label className="toggle-switch">
                                            <input
                                                type="checkbox"
                                                checked={badgesVisibleToAll}
                                                onChange={(e) => updateBadgesVisibleToAll(e.target.checked)}
                                                disabled={isSavingAdminSettings}
                                            />
                                            <span className="toggle-slider"></span>
                                        </label>
                                        <span style={{
                                            color: badgesVisibleToAll ? 'var(--Green-600, #16A34A)' : 'var(--Gray-500, #667085)',
                                            fontWeight: '500',
                                            fontSize: '14px'
                                        }}>
                                            {badgesVisibleToAll ? t('settings.visibleToAll') : t('settings.adminOnly')}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="preference-note">
                            <i className="ti ti-info-circle" style={{ fontSize: '16px', marginRight: '8px', color: 'var(--Primary-600)' }}></i>
                            Current status: Badges are {badgesVisibleToAll ? 'visible to all users' : 'only visible to administrators'}.
                        </div>
                    </div>

                    {/* Watermark Detection Settings */}
                    <div className="preference-card">
                        <div className="preference-header">
                            <h4>{t('settings.watermarkTitle')}</h4>
                            <p>{t('settings.watermarkDesc')}</p>
                        </div>

                        {isLoadingWatermarkSettings ? (
                            <div className="loading-spinner-small" style={{ margin: '20px auto' }}></div>
                        ) : (
                            <>
                                <div className="preference-item">
                                    <div className="preference-info">
                                        <h5>{t('settings.confidenceThreshold')}</h5>
                                        <p>{t('settings.confidenceThresholdDesc')}</p>
                                    </div>
                                    <div className="preference-control">
                                        <div className="numeric-input-group">
                                            <input
                                                type="number"
                                                min="0"
                                                max="1"
                                                step="0.05"
                                                value={watermarkSettings.confidenceThreshold}
                                                onChange={(e) => setWatermarkSettings({ ...watermarkSettings, confidenceThreshold: parseFloat(e.target.value) || 0.6 })}
                                                disabled={isSavingWatermarkSettings}
                                                className="numeric-input"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="preference-item">
                                    <div className="preference-info">
                                        <h5>{t('settings.uniformityThreshold')}</h5>
                                        <p>{t('settings.uniformityThresholdDesc')}</p>
                                    </div>
                                    <div className="preference-control">
                                        <div className="numeric-input-group">
                                            <input
                                                type="number"
                                                min="0"
                                                max="1"
                                                step="0.05"
                                                value={watermarkSettings.uniformityRatioThreshold}
                                                onChange={(e) => setWatermarkSettings({ ...watermarkSettings, uniformityRatioThreshold: parseFloat(e.target.value) || 0.75 })}
                                                disabled={isSavingWatermarkSettings}
                                                className="numeric-input"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="preference-item">
                                    <div className="preference-info">
                                        <h5>{t('settings.colorTolerance')}</h5>
                                        <p>{t('settings.colorToleranceDesc')}</p>
                                    </div>
                                    <div className="preference-control">
                                        <div className="numeric-input-group">
                                            <input
                                                type="number"
                                                min="0"
                                                max="50"
                                                step="1"
                                                value={watermarkSettings.colorTolerance}
                                                onChange={(e) => setWatermarkSettings({ ...watermarkSettings, colorTolerance: parseInt(e.target.value) || 20 })}
                                                disabled={isSavingWatermarkSettings}
                                                className="numeric-input"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="preference-item">
                                    <div className="preference-info">
                                        <h5>{t('settings.resolutionWeight')}</h5>
                                        <p>{t('settings.resolutionWeightDesc')}</p>
                                    </div>
                                    <div className="preference-control">
                                        <div className="numeric-input-group">
                                            <input
                                                type="number"
                                                min="0"
                                                max="1"
                                                step="0.1"
                                                value={watermarkSettings.resolutionMatchWeight}
                                                onChange={(e) => setWatermarkSettings({ ...watermarkSettings, resolutionMatchWeight: parseFloat(e.target.value) || 0.5 })}
                                                disabled={isSavingWatermarkSettings}
                                                className="numeric-input"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="preference-item">
                                    <div className="preference-info">
                                        <h5>{t('settings.detectionWeight')}</h5>
                                        <p>{t('settings.detectionWeightDesc')}</p>
                                    </div>
                                    <div className="preference-control">
                                        <div className="numeric-input-group">
                                            <input
                                                type="number"
                                                min="0"
                                                max="1"
                                                step="0.1"
                                                value={watermarkSettings.watermarkConfidenceWeight}
                                                onChange={(e) => setWatermarkSettings({ ...watermarkSettings, watermarkConfidenceWeight: parseFloat(e.target.value) || 0.5 })}
                                                disabled={isSavingWatermarkSettings}
                                                className="numeric-input"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="preference-actions">
                                    <button
                                        onClick={() => updateWatermarkSettings(watermarkSettings)}
                                        disabled={isSavingWatermarkSettings}
                                        className="save-button primary"
                                    >
                                        {isSavingWatermarkSettings ? t('common.saving') : t('settings.saveDetectionSettings')}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Section 5: Feedback and Connections */}
            <div className="settings-section">
                <h2>
                    <i className="ti ti-message-circle" style={{ fontSize: '24px', marginRight: '8px', color: 'var(--Primary-600)' }}></i>
                    {t('settings.feedbackConnections')}
                </h2>
                <p className="section-description">{t('settings.feedbackSubtitle')}</p>

                <div className="community-cards">
                    <div className="feedback-card">
                        <div className="feedback-content">
                            <div className="feedback-icon">
                                <DiscordButton
                                    variant="icon-only"
                                    className="discord-icon-only"
                                    children=""
                                    onClick={() => {
                                        posthog?.capture('discord_link_clicked', {
                                            source: 'settings_page',
                                            page: 'settings'
                                        });
                                    }}
                                />
                            </div>
                            <div className="feedback-text">
                                <h3>{t('settings.joinDiscordTitle')}</h3>
                                <p>
                                    {t('settings.joinDiscordDesc')}
                                </p>
                                <DiscordButton
                                    onClick={() => {
                                        posthog?.capture('discord_link_clicked', {
                                            source: 'settings_page',
                                            page: 'settings'
                                        });
                                    }}
                                >
                                    {t('settings.joinDiscordCta')}
                                </DiscordButton>
                            </div>
                        </div>
                    </div>

                    <div className="feedback-card">
                        <div className="feedback-content">
                            <div className="feedback-icon">
                                <RedditButton
                                    variant="icon-only"
                                    className="reddit-icon-only"
                                    children=""
                                    onClick={() => {
                                        posthog?.capture('reddit_link_clicked', {
                                            source: 'settings_page',
                                            page: 'settings'
                                        });
                                    }}
                                />
                            </div>
                            <div className="feedback-text">
                                <h3>{t('settings.joinRedditTitle')}</h3>
                                <p>
                                    {t('settings.joinRedditDesc')}
                                </p>
                                <RedditButton
                                    onClick={() => {
                                        posthog?.capture('reddit_link_clicked', {
                                            source: 'settings_page',
                                            page: 'settings'
                                        });
                                    }}
                                >
                                    {t('settings.joinRedditCta')}
                                </RedditButton>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Section 6: Support Development */}
            <div className="settings-section">
                <h2>
                    <i className="ti ti-coffee" style={{ fontSize: '24px', marginRight: '8px', color: 'var(--Primary-600)' }}></i>
                    {t('settings.supportTitle')}
                </h2>
                <p className="section-description">{t('settings.supportSubtitle')}</p>

                <div className="preference-card">
                    <div className="preference-item">
                        <div className="preference-info">
                            <h4>{t('settings.buyCoffeeTitle')}</h4>
                            <p>
                                {t('settings.buyCoffeeDesc')}
                            </p>
                        </div>
                        <div className="preference-control">
                            <a href="https://www.buymeacoffee.com/mythicmaps" target="_blank" rel="noopener noreferrer">
                                <img
                                    src="https://cdn.buymeacoffee.com/buttons/v2/default-yellow.png"
                                    alt="Buy Me A Coffee"
                                    style={{ height: '60px', width: '217px' }}
                                />
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            {/* Section 7: Danger Zone */}
            <div className="settings-section danger-zone">
                <h2 style={{ color: '#dc2626' }}>
                    <i className="ti ti-alert-triangle" style={{ fontSize: '24px', marginRight: '8px' }}></i>
                    {t('settings.dangerZone')}
                </h2>
                <p className="section-description" style={{ color: '#991b1b' }}>{t('settings.dangerDesc')}</p>

                <div className="danger-card">
                    <div className="danger-content">
                        <h3>{t('settings.deleteAccount')}</h3>
                        <p>
                            {t('settings.deleteAccountDesc')}
                        </p>
                        <button
                            onClick={() => setShowDeleteModal(true)}
                            className="danger-button"
                        >
                            {t('settings.deleteMyAccountCta')}
                        </button>
                    </div>
                </div>
            </div>

            {/* Delete Account Modal */}
            {showDeleteModal && (
                <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
                    <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
                        <h2>{t('settings.deleteAccount')}</h2>
                        <div className="warning-banner">
                            <i className="ti ti-alert-triangle" style={{ fontSize: '24px' }}></i>
                            <p>{t('settings.cannotUndo')}</p>
                        </div>

                        <div className="deletion-details">
                            <h4>{t('settings.whatWillBeDeleted')}</h4>
                            <ul>
                                <li>{t('settings.deletedItemMaps')}</li>
                                <li>{t('settings.deletedItemPersonal')}</li>
                                <li>{t('settings.deletedItemStrava')}</li>
                            </ul>

                            <h4>{t('settings.whatWillBePreserved')}</h4>
                            <ul>
                                <li>{t('settings.preservedItemCredits')}</li>
                                <li>{t('settings.preservedItemMonthly')}</li>
                                <li>{t('settings.preservedItemRewards')}</li>
                            </ul>

                            <p className="info-text">{t('settings.restoreInfo')}</p>
                        </div>

                        <div className="confirmation-input">
                            <label>{t('settings.typeDeleteToConfirm', { word: 'DELETE' })}</label>
                            <input
                                type="text"
                                value={deleteConfirmText}
                                onChange={(e) => setDeleteConfirmText(e.target.value)}
                                placeholder={t('settings.typeDeletePlaceholder', { word: 'DELETE' })}
                                disabled={isDeletingAccount}
                            />
                        </div>

                        <div className="modal-actions">
                            <button
                                onClick={() => {
                                    setShowDeleteModal(false);
                                    setDeleteConfirmText('');
                                }}
                                className="cancel-button"
                                disabled={isDeletingAccount}
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                onClick={handleDeleteAccount}
                                className="confirm-delete-button"
                                disabled={deleteConfirmText !== 'DELETE' || isDeletingAccount}
                            >
                                {isDeletingAccount ? t('common.deleting') : t('settings.deleteMyAccountCta')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
}

export default SettingsPage; 